import {
  AbstractMesh,
  AnimationGroup,
  Color4,
  CubeTexture,
  IParticleSystem,
  ISceneLoaderProgressEvent,
  Mesh,
  PointsCloudSystem,
  Scene,
  SceneLoader,
  SceneLoaderSuccessCallback,
  Skeleton,
  Sound,
  Texture,
  Vector3,
  VideoTexture,
  VideoTextureSettings,
  TransformNode,
} from "babylonjs";
import "babylonjs-loaders";
import { parse } from "@loaders.gl/core";
import { PLYLoader } from "@loaders.gl/ply";
import Utils, { IS_BABYLON_NATIVE_JSCORE } from "../utils/utils";

//----------------------------------------------------------------
export interface IAssetBase {
  type: "model" | "pointcloud" | "texture" | "cubetexture" | "video" | "audio";
  path: string;
  id?: string;
  loaded?: boolean;
  loading?: boolean;
  loadProgress?: number;
  abortCtrl?: AbortController;
}

export interface IAssetPointCloud extends IAssetBase {
  size?: number;
  color?: [number, number, number];
  pcs?: PointsCloudSystem;
  mesh?: Mesh;
}

export interface IAssetModel extends IAssetBase {
  meshes?: AbstractMesh[];
  meshRoot?: AbstractMesh;
  animations?: AnimationGroup[];
  skeletons?: Skeleton[];
  extension?: string; // optional explicit extension (eg. ".glb") for extensionless URLs.
}

export interface IAssetTexture extends IAssetBase {
  texture?: Texture;
}

export interface IAssetCubeTexture extends IAssetBase {
  cubeTexture?: CubeTexture;
}

export interface IAssetVideo extends IAssetBase {
  videoTexture?: VideoTexture;
  videoElement?: HTMLVideoElement;
  // Mipmaps + anisotropic filtering keep the video clean when it is minified
  // (small on screen / viewed obliquely): without mips, sampling skips texels
  // and the video looks pixelated from a distance. Babylon regenerates the
  // mips on every video frame. Defaults: on, 4x. Set false / 1 to opt out.
  generateMipMaps?: boolean;
  anisotropicFilteringLevel?: number;
}

export interface IAssetAudio extends IAssetBase {
  audio?: Sound;
  audioElement?: HTMLAudioElement;
}

//----------------------------------------------------------------
export async function loadModel(asset: IAssetModel, scene: Scene, rootNode: TransformNode): Promise<void> {
  try {
    if (IS_BABYLON_NATIVE_JSCORE) {
      // Draco GLBs hang forever on native with Babylon's stock decoder
      // bootstrap (async wasm/worker paths don't work on JSCore) — swap in
      // the bundled pure-JS decoder before the glTF loader constructs its
      // KHR_draco extension. Dynamic import: web builds keep this 500KB
      // chunk out of the main bundle and never fetch it.
      const { configureNativeDracoDecoder } = await import("./loader.draco.native");
      configureNativeDracoDecoder();
    }
    let file: File | undefined;
    const useFetch = false; // use fetch to load the model.
    if (useFetch) {
      // loading a model using fetch casuses a memory leak.
      // we came across this issue in babylonjs 7.45.0 and 7.50.0
      asset.abortCtrl = new AbortController();
      const { signal } = asset.abortCtrl;
      const response = await fetch(asset.path, { signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch model: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const fileName = `model-${Utils.UUID()}.glb`; // needs a unique name to avoid caching issues.
      file = new File([blob], fileName, { type: "model/gltf-binary" });
    }
    return new Promise<void>((resolve, reject) => {
      const onSuccess: SceneLoaderSuccessCallback = (
        meshes: AbstractMesh[],
        _particleSystems: IParticleSystem[],
        skeletons: Skeleton[],
        animationGroups: AnimationGroup[]
      ) => {
        meshes.forEach(mesh => {
          if (!mesh.parent) {
            mesh.parent = rootNode;
            asset.meshRoot = mesh;
          }
        });
        animationGroups.forEach(animGroup => {
          animGroup.stop(); // stop from auto-playing.
        });
        asset.meshes = meshes;
        asset.animations = animationGroups;
        asset.skeletons = skeletons;
        resolve();
      };
      const onProgress = (_event: ISceneLoaderProgressEvent) => {
        //console.log("app.content.loadModel.progress: " + event.loaded);
      };
      const onError = (_scene: Scene, message: string, _exception?: any) => {
        console.log("app.content.loadModel.error: " + message);
        reject(new Error(message));
      };
      if (file) {
        SceneLoader.ImportMesh("", "", file, scene, onSuccess, onProgress, onError, asset.extension);
      } else {
        SceneLoader.ImportMesh("", asset.path, undefined, scene, onSuccess, onProgress, onError, asset.extension);
      }
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      console.log("app.content.loadModel.abort: ", asset.id);
      return;
    }
    console.error("app.content.loadModel.error: ", error);
    throw error;
  } finally {
    asset.abortCtrl = undefined;
  }
}

export async function loadPointCloud(asset: IAssetPointCloud, scene: Scene, rootNode: TransformNode): Promise<void> {
  asset.abortCtrl = new AbortController();
  const { signal } = asset.abortCtrl;
  try {
    const response = await fetch(asset.path, { signal });
    // Hand loaders.gl the raw bytes rather than the Response (works on both
    // targets; Babylon Native's fetch polyfill responses aren't Response-class
    // instances loaders.gl can validate). On native only, force inline parsing:
    // loaders.gl would otherwise boot its parser worker from a CDN/blob URL,
    // which isn't supported there. Web keeps worker parsing as before.
    const buffer = await response.arrayBuffer();
    const data = await parse(buffer, PLYLoader, { worker: !IS_BABYLON_NATIVE_JSCORE });
    const pos = data.attributes.POSITION.value;
    const col = data.attributes.COLOR_0.value;
    const num = pos.length / 3;

    const pointCloudSize = asset.size ?? 1;
    const pcs = new PointsCloudSystem("pcs", pointCloudSize, scene);
    pcs.addPoints(num, (particle: { position: Vector3; color: Color4 }, i: number) => {
      const j = 3 * i;
      particle.position = new Vector3(pos[j + 0], pos[j + 1], pos[j + 2]);
      particle.color = new Color4(
        asset.color ? asset.color[0] : col[j + 0] / 255.0,
        asset.color ? asset.color[1] : col[j + 1] / 255.0,
        asset.color ? asset.color[2] : col[j + 2] / 255.0,
        1.0
      );
    });
    const mesh = await pcs.buildMeshAsync();
    mesh.parent = rootNode;
    asset.pcs = pcs;
    asset.mesh = mesh;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      console.log("app.content.loadPointCloud.abort: ", asset.id);
      return;
    }
    console.error("app.content.loadPointCloud.error: ", error);
    throw error;
  } finally {
    asset.abortCtrl = undefined;
  }
}

// Optional texture params. All default to the prior behaviour, so existing
// callers are unaffected; the editor passes invertY/hasAlpha/sampling to match
// its panels (which flip via invertY rather than uScale/vScale).
export interface LoadTextureOptions {
  noMipmap?: boolean;
  invertY?: boolean;
  samplingMode?: number;
  hasAlpha?: boolean;
}

export async function loadTexture(
  asset: IAssetTexture,
  scene: Scene,
  opts: LoadTextureOptions = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const onLoad = () => {
      if (opts.hasAlpha) texture.hasAlpha = true;
      asset.texture = texture;
      resolve();
    };
    const onError = (message?: string, _exception?: any) => {
      reject(new Error(`Failed to load texture: ${message}`));
    };
    const texture = new Texture(asset.path, scene, opts.noMipmap, opts.invertY ?? false, opts.samplingMode, onLoad, onError);
  });
}

export async function loadCubeTexture(asset: IAssetCubeTexture, scene: Scene): Promise<void> {
  return new Promise((resolve, reject) => {
    const onLoad = () => {
      asset.cubeTexture = cubeTexture;
      resolve();
    };
    const onError = (message?: string, _exception?: any) => {
      reject(new Error(`Failed to load texture: ${message}`));
    };
    const cubeTexture = new CubeTexture(asset.path, scene, undefined, false, undefined, onLoad, onError);
  });
}

export async function loadVideo(asset: IAssetVideo, scene: Scene): Promise<void> {
  asset.abortCtrl = new AbortController();
  let isAborted = false;
  
  asset.abortCtrl.signal.addEventListener('abort', () => {
    isAborted = true;
  });
  
  return new Promise((resolve, reject) => {
    let readyTimeout: ReturnType<typeof setTimeout> | undefined;
    const addEventListeners = (videoTexture: VideoTexture) => {
      videoTexture.video.addEventListener("loadedmetadata", onLoad, { once: true }); // iOS fix.
      videoTexture.video.addEventListener("canplay", onLoad, { once: true });
      videoTexture.video.addEventListener("error", onError, { once: true });
    };
    const removeEventListeners = (videoTexture: VideoTexture) => {
      if (readyTimeout !== undefined) {
        clearTimeout(readyTimeout);
        readyTimeout = undefined;
      }
      videoTexture.video.removeEventListener("loadedmetadata", onLoad);
      videoTexture.video.removeEventListener("canplay", onLoad);
      videoTexture.video.removeEventListener("error", onError);
    };
    const onLoad = (_event?: Event) => {
      removeEventListeners(videoTexture);
      if (isAborted) {
        resolve();
        return;
      }
      asset.videoTexture = videoTexture;
      videoTexture.video.pause();
      resolve();
    };
    const onError = (_e?: any) => {
      removeEventListeners(videoTexture);
      if (isAborted) {
        resolve();
        return;
      }
      reject(new Error(`Failed to load video: ${_e}`));
    };
    const settings: VideoTextureSettings = {
      autoPlay: true,
      autoUpdateTexture: true,
    };
    let videoSrc: string | HTMLVideoElement = asset.path;
    if (asset.videoElement) {
      // when a video element is provided, use it as the source.
      // this allows for one video element to be used for multiple video textures,
      // it can also be reused to load and play different videos over the app lifecycle.
      // this is useful when video elements need to be initialised on user interaction and then can be reused.
      videoSrc = asset.videoElement;
      videoSrc.src = asset.path;
    }
    const videoTexture = new VideoTexture(
      "video", // name
      videoSrc, // src
      scene, // scene
      asset.generateMipMaps ?? true, // generateMipMaps (see IAssetVideo)
      undefined, // invertY
      Texture.TRILINEAR_SAMPLINGMODE, // samplingMode
      settings, // settings
      onError, // onError
      undefined // format
    );
    videoTexture.anisotropicFilteringLevel = asset.anisotropicFilteringLevel ?? 4;
    addEventListeners(videoTexture);
    // The ready events fire exactly once on the Babylon Native pipeline — if
    // the video became ready before the listeners attached (timing shifts
    // between Debug/Release builds), they are already gone. readyState is the
    // truth: >= 1 (HAVE_METADATA; the native polyfill reports 10 when ready).
    if (videoTexture.video.readyState >= 1) {
      onLoad();
      return;
    }
    // Belt and braces: never let one silent video park the whole experience —
    // settle after 10s and let the texture catch up when frames arrive.
    readyTimeout = setTimeout(() => {
      console.warn(`loadVideo: no ready event after 10s, continuing: ${asset.path}`);
      onLoad();
    }, 10000);
  });
}

export async function loadAudio(asset: IAssetAudio, scene: Scene): Promise<void> {
  asset.abortCtrl = new AbortController();
  let isAborted = false;

  asset.abortCtrl.signal.addEventListener("abort", () => {
    isAborted = true;
  });

  return new Promise((resolve, reject) => {
    try {
      // If audioElement is provided, use HTMLAudioElement instead of Babylon Sound
      if (asset.audioElement) {
        const audioElement = asset.audioElement;
        const addEventListeners = (el: HTMLAudioElement) => {
          el.addEventListener("loadedmetadata", onLoad, { once: true });
          el.addEventListener("canplay", onLoad, { once: true });
          el.addEventListener("error", onError, { once: true });
        };
        const removeEventListeners = (el: HTMLAudioElement) => {
          el.removeEventListener("loadedmetadata", onLoad);
          el.removeEventListener("canplay", onLoad);
          el.removeEventListener("error", onError);
        };
        const onLoad = (_event: Event) => {
          removeEventListeners(audioElement);
          if (isAborted) {
            resolve();
            return;
          }
          // Ensure audio is not playing automatically
          audioElement.pause();
          resolve();
        };
        const onError = (e: Event) => {
          removeEventListeners(audioElement);
          if (isAborted) {
            resolve();
            return;
          }
          reject(new Error(`Failed to load audio: ${e}`));
        };
        audioElement.src = asset.path;
        addEventListeners(audioElement);
        audioElement.load();
      } else {
        // Use Babylon Sound as fallback
        const onLoad = () => {
          if (isAborted) {
            resolve();
            return;
          }
          asset.audio = audio;
          resolve();
        };
        const audio = new Sound("audio", asset.path, scene, onLoad, {
          loop: false,
          autoplay: false,
          volume: 1,
        });
        // Note: Babylon Sound doesn't expose DOM events here; abort simply resolves.
      }
    } catch (e) {
      if (isAborted) {
        resolve();
        return;
      }
      reject(new Error(`Failed to load audio: ${e}`));
    }
  });
}

//----------------------------------------------------------------
function unloadBase(asset: IAssetBase) {
  if (asset.abortCtrl) {
    asset.abortCtrl.abort();
    asset.abortCtrl = undefined;
  }
}

export function unloadModel(asset: IAssetModel) {
  unloadBase(asset);
  if (asset.meshes) {
    asset.meshes.forEach(mesh => {
      mesh.dispose(false, true);
    });
  }
  if (asset.animations) {
    asset.animations.forEach(animGroup => {
      animGroup.stop();
      animGroup.dispose();
    });
  }
  if (asset.skeletons) {
    asset.skeletons.forEach(skeleton => {
      skeleton.dispose();
    });
  }
  asset.meshes = undefined;
  asset.meshRoot = undefined;
  asset.animations = undefined;
  asset.skeletons = undefined;
}

export function unloadPointCloud(asset: IAssetPointCloud) {
  unloadBase(asset);
  if (asset.pcs) {
    asset.pcs.dispose();
  }
  if (asset.mesh) {
    asset.mesh.dispose(false, true);
  }
  asset.mesh = undefined;
}

export function unloadTexture(asset: IAssetTexture) {
  unloadBase(asset);
  if (asset.texture) {
    asset.texture.dispose();
    asset.texture = undefined;
  }
}

export function unloadCubeTexture(asset: IAssetCubeTexture) {
  unloadBase(asset);
  if (asset.cubeTexture) {
    asset.cubeTexture.dispose();
    asset.cubeTexture = undefined;
  }
}

export function unloadVideo(asset: IAssetVideo) {
  unloadBase(asset);
  if (asset.videoTexture) {
    asset.videoTexture.dispose();
    asset.videoTexture.video.src = "";
    asset.videoTexture.video.load();
    asset.videoTexture = undefined;
  }
}

export function unloadAudio(asset: IAssetAudio) {
  unloadBase(asset);
  if (asset.audio) {
    asset.audio.pause();
    asset.audio.dispose();
    asset.audio = undefined;
  }
  if (asset.audioElement) {
    asset.audioElement.pause();
    asset.audioElement.src = "";
    asset.audioElement.load();
  }
}
